import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import OnlineGameMenu from './OnlineGameMenu';
import { API_URL } from './environment';
import options from './options_ORIG.json';
import betmap from './betmap.json';
import {
  clearPersistedPlayerSession,
  getLaunchTokenFromUrl,
  getPlayerSessionHeaders,
  getStoredPlayerSession,
  isNetworkRequestError,
  isPlayerSessionExpired,
  isSessionAuthorizationError,
  persistPlayerSession,
} from './onlinePlayerSession';
import './UserHistoryPage.css';

const PLAYER_SESSION_EXCHANGE_PATHS = ['/playersessions/exchange', '/api/provider/v1/betconstruct/launch', '/login'];
const ONLINE_PLAYER_HISTORY_CACHE_KEY = 'sun_online_player_history_cache';
const ONLINE_AUTH_REQUEST_TIMEOUT_MS = 8000;

// const PLAYER_HISTORY_PATHS = [
//   '/playersessions/history',
//   '/playersessions/bets/history',
//   '/api/provider/v1/betconstruct/session/history',
//   '/api/provider/v1/betconstruct/bets/history',
// ];

const PLAYER_HISTORY_PATHS = [
  '/userhistorydata',
  '/playersessions/history',
  '/playersessions/bets/history',
  '/api/provider/v1/betconstruct/session/history',
  '/api/provider/v1/betconstruct/bets/history',
];

const BET_LABELS = {
  SINGLE37: '0',
  LOW: '1-18',
  HIGH: '19-36',
  EVEN: 'Even',
  ODD: 'Odd',
  RED: 'Red',
  BLACK: 'Black',
  DOZEN1: '1st 12',
  DOZEN2: '2nd 12',
  DOZEN3: '3rd 12',
  COLUMN1: 'C1',
  COLUMN2: 'C2',
  COLUMN3: 'C3',
  REDSPLIT: 'Red Split',
  BLACKSPLIT: 'Black Split',
  ZEROSPIEL: 'Zero Spiel',
  SNAKE: 'Snake',
  ORPHELINS: 'Orphelins',
  TIERS: 'Tiers',
  VOISINS: 'Voisins',
};

const NUMBER_COMBINATION_BET_PATTERN = /^(?:(?:SPLIT|CORNER|STREET|SIXLINE)\d+|FIRSTFOUR)$/;

const getStoredJson = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const getAmountValue = (...values) => {
  for (const value of values) {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }

    if (value && typeof value === 'object') {
      const amount = getAmountValue(value.amount, value.value, value.total);
      if (amount !== null) {
        return amount;
      }
    }
  }

  return null;
};

const formatMoney = (value) => {
  const amount = getAmountValue(value);

  if (amount === null) {
    return '-';
  }

  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNumberColor = (number) => {
  const match = options.find(item => item.number === String(number));
  return match ? match.color : 'black';
};

const formatBetLabel = (betKey) => {
  const combinationNumbers = betmap[betKey]?.numbers;
  if (NUMBER_COMBINATION_BET_PATTERN.test(betKey) && Array.isArray(combinationNumbers)) {
    return combinationNumbers.join(', ');
  }

  if (BET_LABELS[betKey]) {
    return BET_LABELS[betKey];
  }

  if (/^SINGLE\d+$/.test(betKey)) {
    return betKey.replace('SINGLE', '');
  }

  return String(betKey)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
};

const normalizeBets = (bets) => {
  if (Array.isArray(bets)) {
    return bets.reduce((result, bet, index) => {
      const betKey = bet.betKey || bet.key || bet.selection || bet.betType || `BET-${index + 1}`;
      const stake = getAmountValue(bet.stake, bet.amount, bet.value, bet.betAmount) || 0;

      return {
        ...result,
        [betKey]: (result[betKey] || 0) + stake,
      };
    }, {});
  }

  if (bets && typeof bets === 'object') {
    return bets;
  }

  return {};
};

const sumBets = (bets) => {
  return Object.values(bets || {}).reduce((sum, value) => sum + (getAmountValue(value) || 0), 0);
};

const calculatePayouts = (bets, winningNumber) => {
  const resultNumber = String(winningNumber);

  return Object.entries(bets || {}).reduce(
    (summary, [betKey, value]) => {
      const stake = getAmountValue(value) || 0;
      const bet = betmap[betKey];
      const isWinningBet = bet?.numbers?.map(String).includes(resultNumber);
      const payout = isWinningBet ? stake * ((Number(bet.payout) || 0) + 1) : 0;

      return {
        payoutAmount: summary.payoutAmount + payout,
        payouts: {
          ...summary.payouts,
          [betKey]: payout,
        },
      };
    },
    { payoutAmount: 0, payouts: {} }
  );
};

const normalizeOutcome = (item, payoutAmount, resultNumber) => {
  const rawOutcome = String(item.outcome || item.resultStatus || item.settlementStatus || item.status || '').toUpperCase();
  const hasPayoutBreakdown = Boolean(item.payouts && typeof item.payouts === 'object' && Object.keys(item.payouts).length);
  const hasSettlementAmount = getAmountValue(
    item.payoutAmount,
    item.totalPayoutAmount,
    item.totalPayout,
    item.payout,
    item.winAmount,
    item.megaJackpotAmount,
    item.miniJackpotAmount,
    item.settlement?.payout
  );
  const hasSettlementData = Boolean(
    hasPayoutBreakdown ||
    (hasSettlementAmount !== null && hasSettlementAmount > 0) ||
    item.settledAt ||
    item.completedAt ||
    item.redeemedAt
  );

  if (rawOutcome.includes('REFUND') || rawOutcome.includes('VOID') || rawOutcome.includes('CANCEL')) {
    return 'REFUND';
  }

  if (rawOutcome.includes('WIN')) {
    return 'WIN';
  }

  if (rawOutcome.includes('LOSS') || rawOutcome.includes('LOSE') || rawOutcome.includes('LOST')) {
    return 'LOSS';
  }

  if ((rawOutcome.includes('PENDING') || rawOutcome.includes('ACCEPTED')) && !hasSettlementData) {
    return 'PENDING';
  }

  if ((resultNumber === null || resultNumber === undefined) && !hasSettlementData) {
    return 'PENDING';
  }

  return payoutAmount > 0 ? 'WIN' : 'LOSS';
};

const normalizeHistoryItem = (item, index) => {
  const bets = normalizeBets(item.bets || item.placedBets || item.betSelections || item.selections);
  const betAmount = getAmountValue(
    item.betAmount,
    item.totalStake,
    item.stake,
    item.amount,
    item.totalBetAmount
  ) || sumBets(bets);
  const resultNumber = item.resultNumber ?? item.winningNumber ?? item.result?.number ?? item.round?.winningNumber ?? null;
  const computedSettlement = resultNumber !== null ? calculatePayouts(bets, resultNumber) : { payoutAmount: 0, payouts: {} };
  const payoutAmount = getAmountValue(
    item.payoutAmount,
    item.totalPayoutAmount,
    item.totalPayout,
    item.payout,
    item.winAmount,
    item.settlement?.payout
  ) ?? computedSettlement.payoutAmount;
  const jackpotPayoutAmount = (
    (getAmountValue(item.megaJackpotAmount) || 0) +
    (getAmountValue(item.miniJackpotAmount) || 0)
  );
  const totalPayoutAmount = payoutAmount + jackpotPayoutAmount;
  const outcome = normalizeOutcome(item, totalPayoutAmount, resultNumber);

  return {
    id: item.id || item._id || item.ticketId || item.clientRequestId || `history-${index}`,
    ticketId: item.ticketId || item.ticketNumber || item.nanoId || '-',
    spinNumber: item.spinNumber || item.spinId || item.roundId || item.round?.spinNumber || '-',
    createdAt: item.createdAt || item.acceptedAt || item.settledAt || item.updatedAt,
    bets,
    betAmount,
    resultNumber,
    payoutAmount: totalPayoutAmount,
    basePayoutAmount: payoutAmount,
    jackpotPayoutAmount,
    outcome,
    status: item.status || item.settlementStatus || outcome,
  };
};

const normalizeHistoryResponse = (data) => {
  const rawItems = Array.isArray(data)
    ? data
    : data?.history || data?.items || data?.bets || data?.roundBets || data?.tickets || [];

  return rawItems.map(normalizeHistoryItem);
};

const computeStats = (history) => {
  const settledHistory = history.filter(item => item.outcome !== 'PENDING');
  const totalBetAmount = history.reduce((sum, item) => sum + (Number(item.betAmount) || 0), 0);
  const totalPayoutAmount = history.reduce((sum, item) => sum + (Number(item.payoutAmount) || 0), 0);
  const wins = history.filter(item => item.outcome === 'WIN').length;
  const losses = history.filter(item => item.outcome === 'LOSS').length;
  const pending = history.filter(item => item.outcome === 'PENDING').length;
  const net = totalPayoutAmount - totalBetAmount;
  const winRate = settledHistory.length ? Math.round((wins / settledHistory.length) * 100) : 0;

  return {
    rounds: history.length,
    wins,
    losses,
    pending,
    totalBetAmount,
    totalPayoutAmount,
    net,
    winRate,
  };
};

const normalizePlayerSession = (data = {}) => {
  const tokenHash = data.tokenHash || data.session?.tokenHash || '';
  const shop = data.shop || data.bettingShop || data.playerSession?.shop || (Array.isArray(data.bettingShops) ? data.bettingShops[0] : null);
  const player = data.player || data.onlinePlayer || null;
  const agentID = data.agentID || data.agentId || player?.agentID || player?.agentId || '';
  const bettingShopID = data.bettingShopID || data.bettingShopId || shop?.bettingShopId || shop?._id || '';

  return {
    tokenHash,
    playerSessionId: data.playerSessionId || data.playerSession?._id || data.session?._id || '',
    expiredAt: data.expiredAt || data.session?.expiredAt || data.expiresAt || data.session?.expiresAt || null,
    launchToken: data.launchToken || data.session?.launchToken || '',
    player,
    shop,
    balance: data.balance ?? data.playerBalance ?? 10000,
    agentID,
    agentId: agentID,
    bettingShopID,
    raw: data,
  };
};

const getHistoryCacheIdentity = (session) => (
  session?.playerSessionId || session?.raw?.playerSessionId || session?.tokenHash || ''
);

const getCachedHistory = (session) => {
  const cache = getStoredJson(ONLINE_PLAYER_HISTORY_CACHE_KEY, null);
  const identity = getHistoryCacheIdentity(session);

  if (!cache || !identity || cache.identity !== identity || !Array.isArray(cache.history)) {
    return null;
  }

  return cache.history;
};

const persistHistoryCache = (session, history) => {
  const identity = getHistoryCacheIdentity(session);
  if (!identity) return;

  try {
    localStorage.setItem(ONLINE_PLAYER_HISTORY_CACHE_KEY, JSON.stringify({
      identity,
      history,
      savedAt: Date.now(),
    }));
  } catch (error) {
    // History is still rendered from memory when persistent storage is unavailable.
  }
};

function UserHistoryPage() {
  const initialContextRef = useRef(null);

  if (!initialContextRef.current) {
    const launchToken = getLaunchTokenFromUrl();
    const storedSession = getStoredPlayerSession();
    const initialSession = storedSession?.tokenHash
      && storedSession.launchToken === launchToken
      && !isPlayerSessionExpired(storedSession)
      ? storedSession
      : null;
    const recoveryTokenHash = storedSession?.launchToken === launchToken
      ? storedSession.tokenHash || ''
      : '';

    initialContextRef.current = {
      launchToken,
      recoveryTokenHash,
      initialSession,
      initialHistory: getCachedHistory(initialSession),
    };
  }

  const { launchToken, recoveryTokenHash, initialSession, initialHistory } = initialContextRef.current;
  const pageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(initialHistory === null);
  const [errorMessage, setErrorMessage] = useState('');
  const [history, setHistory] = useState(initialHistory || []);
  const [session, setSession] = useState(initialSession);

  useEffect(() => {
    let isMounted = true;
    document.body.classList.add('user-history-active');
    window.scrollTo(0, 0);

    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, left: 0 });
    }

    const fetchFirstAvailable = async (paths, config = {}) => {
      let lastError;

      for (const path of paths) {
        try {
          return await axios.get(`${API_URL}${path}`, config);
        } catch (error) {
          lastError = error;
          if (error?.response?.status !== 404) {
            throw error;
          }
        }
      }

      throw lastError;
    };

    const postFirstAvailable = async (paths, payload = {}, config = {}) => {
      let lastError;

      for (const path of paths) {
        try {
          return await axios.post(`${API_URL}${path}`, payload, config);
        } catch (error) {
          lastError = error;
          if (error?.response?.status !== 404) {
            throw error;
          }
        }
      }

      throw lastError;
    };

    const exchangeLaunchTokenForSession = async (nextLaunchToken, tokenHash = '') => {
      const response = await postFirstAvailable(PLAYER_SESSION_EXCHANGE_PATHS, {
        provider: 'sunny-online',
        launchToken: nextLaunchToken,
        gameCode: 'sunny-roulette',
      }, {
        headers: getPlayerSessionHeaders({ tokenHash }),
        timeout: ONLINE_AUTH_REQUEST_TIMEOUT_MS,
      });
      const nextSession = normalizePlayerSession(response?.data);

      if (!nextSession.tokenHash) {
        throw new Error('Player tokenHash is missing in launch response');
      }

      const authenticatedSession = {
        ...nextSession,
        launchToken: nextLaunchToken,
      };

      persistPlayerSession(authenticatedSession);
      return authenticatedSession;
    };

    const fetchHistory = (activeSession) => fetchFirstAvailable(PLAYER_HISTORY_PATHS, {
      params: { limit: 50 },
      headers: getPlayerSessionHeaders(activeSession),
      timeout: ONLINE_AUTH_REQUEST_TIMEOUT_MS,
    });

    const getErrorMessage = (error, fallback) => {
      return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
    };

    const loadHistory = async () => {
      setErrorMessage('');

      if (!launchToken) {
        clearPersistedPlayerSession();
        if (isMounted) {
          setSession(null);
          setHistory([]);
          setErrorMessage('Missing launch token. Open this page with a valid betting shop launch token.');
          setIsLoading(false);
        }
        return;
      }

      let activeSession = initialSession;
      const cachedHistory = initialHistory;

      try {
        if (!activeSession) {
          setIsLoading(true);
          activeSession = await exchangeLaunchTokenForSession(launchToken, recoveryTokenHash);
        }

        if (isMounted) setSession(activeSession);

        let response;
        try {
          response = await fetchHistory(activeSession);
        } catch (error) {
          if (!isSessionAuthorizationError(error)) throw error;

          activeSession = await exchangeLaunchTokenForSession(launchToken, activeSession.tokenHash);
          response = await fetchHistory(activeSession);
        }

        const nextHistory = normalizeHistoryResponse(response?.data);
        const refreshedSession = {
          ...activeSession,
          expiredAt: response?.data?.expiredAt || activeSession.expiredAt,
          balance: response?.data?.balance ?? activeSession.balance,
        };

        persistPlayerSession(refreshedSession);
        persistHistoryCache(refreshedSession, nextHistory);

        if (isMounted) {
          setSession(refreshedSession);
          setHistory(nextHistory);
          setErrorMessage('');
        }
      } catch (error) {
        if (isSessionAuthorizationError(error)) {
          clearPersistedPlayerSession();
        }

        if (isMounted && !(isNetworkRequestError(error) && cachedHistory !== null)) {
          setHistory([]);
          setErrorMessage(getErrorMessage(error, 'Unable to load player history'));
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
      document.body.classList.remove('user-history-active');
    };
  }, [initialHistory, initialSession, launchToken, recoveryTokenHash]);

  const stats = computeStats(history);
  // const player = session?.player || {};
  // const shop = session?.shop || {};
  const launchQueryString = launchToken ? `?launchToken=${encodeURIComponent(launchToken)}` : '';

  const renderBetPills = (bets) => {
    const entries = Object.entries(bets || {});

    if (!entries.length) {
      return <span className="user-history-bet-pill">No bets</span>;
    }

    return entries.map(([key, value]) => (
      <span className="user-history-bet-pill" key={key}>
        <b>{formatBetLabel(key)}</b>
        <em>{formatMoney(value)}</em>
      </span>
    ));
  };

  return (
    <main ref={pageRef} className="user-history-page">
      <header className="user-history-hero">
        <div className="user-history-menu-row">
          <OnlineGameMenu />
        </div>
        <span className="user-history-eyebrow">Online roulette</span>
        <h1>User History</h1>
        {/* <p>
          {player.displayName || player.username || player.providerPlayerId || 'Current player'}
          {shop.name ? ` - ${shop.name}` : ''}
        </p> */}
        <Link className="user-history-back-link" to={`/onlineroulette${launchQueryString}`}>
          Back to game
        </Link>
      </header>

      {isLoading ? (
        <section className="user-history-state-card">
          <strong>Loading history...</strong>
          <span>Checking your player session and recent rounds.</span>
        </section>
      ) : errorMessage ? (
        <section className="user-history-state-card user-history-state-card--error">
          <strong>History unavailable</strong>
          <span>{errorMessage}</span>
        </section>
      ) : (
        <>
          <section className="user-history-stats" aria-label="User statistics">
            <div className="user-history-stat-card">
              <span>Total Bets</span>
              <strong>{formatMoney(stats.totalBetAmount)}</strong>
            </div>
            <div className="user-history-stat-card user-history-stat-card--gold">
              <span>Total Payout</span>
              <strong>{formatMoney(stats.totalPayoutAmount)}</strong>
            </div>
            <div className={`user-history-stat-card ${stats.net >= 0 ? 'user-history-stat-card--win' : 'user-history-stat-card--loss'}`}>
              <span>Net</span>
              <strong>{formatMoney(stats.net)}</strong>
            </div>
            <div className="user-history-stat-card">
              <span>Win Rate</span>
              <strong>{stats.winRate}%</strong>
            </div>
          </section>

          <section className="user-history-overview">
            <div>
              <span>Rounds</span>
              <strong>{stats.rounds}</strong>
            </div>
            <div>
              <span>Wins</span>
              <strong>{stats.wins}</strong>
            </div>
            <div>
              <span>Losses</span>
              <strong>{stats.losses}</strong>
            </div>
            <div>
              <span>Pending</span>
              <strong>{stats.pending}</strong>
            </div>
            <div>
              <span>Balance</span>
              <strong>{formatMoney(session?.balance)}</strong>
            </div>
          </section>

          <section className="user-history-list" aria-label="Spin and bet history">
            <div className="user-history-list__header">
              <h2>Recent Rounds</h2>
              <span>Last {history.length} records</span>
            </div>

            {history.length ? history.map((item) => {
              const outcomeClass = item.outcome.toLowerCase();
              const numberColor = item.resultNumber === null || item.resultNumber === undefined
                ? 'pending'
                : getNumberColor(item.resultNumber);

              return (
                <article className={`user-history-round user-history-round--${outcomeClass}`} key={item.id}>
                  <div className="user-history-round__top">
                    <div>
                      <span>Spin Number</span>
                      <strong>{item.spinNumber}</strong>
                    </div>
                    <b className={`user-history-result user-history-result--${numberColor}`}>
                      {item.resultNumber ?? '-'}
                    </b>
                    <em>{item.outcome}</em>
                  </div>

                  <div className="user-history-round__meta">
                    <span>{formatDate(item.createdAt)}</span>
                    <span>Ticket {item.ticketId}</span>
                  </div>

                  <div className="user-history-round__money">
                    <div>
                      <span>Bet</span>
                      <strong>{formatMoney(item.betAmount)}</strong>
                    </div>
                    <div>
                      <span>Payout</span>
                      <strong>{formatMoney(item.payoutAmount)}</strong>
                    </div>
                  </div>

                  <div className="user-history-bets">
                    {renderBetPills(item.bets)}
                  </div>
                </article>
              );
            }) : (
              <section className="user-history-state-card">
                <strong>No history yet</strong>
                <span>Your accepted bets and settled roulette results will appear here.</span>
              </section>
            )}
          </section>
        </>
      )}
    </main>
  );
}

export default UserHistoryPage;
